import { useQuery } from '@tanstack/react-query';
import { type ReactNode, useEffect, useMemo, useState } from 'react';
import { useStore } from 'zustand';
import { useSession } from '@/features/auth';
import { Label } from '@/shared/ui/primitives/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/ui/primitives/select';
import { companyOptionsQuery } from './company-options-query';
import { resolveCompanyScope, scopeFromKey, scopeKey, scopeOptions } from './company-scope';
import { companyScopeStore } from './company-scope-store';
import { CustomScopeDialog } from './custom-scope-dialog';

/** Valor da opção que abre a seleção personalizada (não é um escopo). */
const CUSTOM_OPTION = 'custom-selection';

/** Rótulo da seleção personalizada ativa. */
function customLabel(count: number): string {
  return `Seleção personalizada (${String(count)} ${count === 1 ? 'empresa' : 'empresas'})`;
}

/**
 * Seletor de escopo no cabeçalho: grupo, matriz + filiais, uma empresa ou uma seleção
 * personalizada, entre as empresas que a API devolve ao usuário. A escolha vale para todas as
 * telas e fica salva entre sessões; um escopo que deixou de ser permitido volta para o grupo.
 */
export function CompanyScopeSelector(): ReactNode {
  const organizationId = useSession((state) => state.session?.organizationId ?? '');
  const canRead = useSession(
    (state) => state.session?.permissions.includes('companies:read') === true,
  );
  const { scope, select } = useStore(companyScopeStore);
  const { data, isSuccess } = useQuery({
    ...companyOptionsQuery(organizationId),
    enabled: canRead && organizationId !== '',
    meta: { silent: true },
  });
  const companies = useMemo(() => data?.items ?? [], [data]);
  const options = useMemo(() => scopeOptions(companies), [companies]);
  const resolved = resolveCompanyScope(scope, companies);
  const isStale = isSuccess && scopeKey(resolved.scope) !== scopeKey(scope);
  const [isChoosing, setChoosing] = useState(false);

  useEffect(() => {
    if (isStale) select(resolved.scope);
  }, [isStale, resolved.scope, select]);

  if (!canRead || companies.length === 0) return null;
  return (
    <div className="flex items-center gap-2">
      <Label htmlFor="company-scope" className="text-sm text-muted-foreground">
        Escopo
      </Label>
      <Select
        value={scopeKey(resolved.scope)}
        onValueChange={(key) => {
          if (key === CUSTOM_OPTION) {
            setChoosing(true);
            return;
          }
          const next = scopeFromKey(key);
          if (next !== null) select(next);
        }}
      >
        <SelectTrigger id="company-scope" className="w-64">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {options.map((option) => (
            <SelectItem key={option.key} value={option.key}>
              {option.label}
            </SelectItem>
          ))}
          {resolved.scope.kind === 'CUSTOM' && (
            <SelectItem value={scopeKey(resolved.scope)}>
              {customLabel(resolved.scope.companyIds.length)}
            </SelectItem>
          )}
          <SelectItem value={CUSTOM_OPTION}>Seleção personalizada…</SelectItem>
        </SelectContent>
      </Select>
      <CustomScopeDialog
        open={isChoosing}
        onOpenChange={setChoosing}
        companies={companies}
        initialCompanyIds={resolved.companyIds ?? []}
        onConfirm={(companyIds) => {
          select({ kind: 'CUSTOM', companyIds });
          setChoosing(false);
        }}
      />
    </div>
  );
}
